import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { HiOutlineCurrencyDollar } from 'react-icons/hi';

import cx from 'classnames';
import {
  CUSTOM_AMOUNT_MESSAGE,
  DonateNowFormSteps,
  DonateNowFormStep,
  IDonateNowForm,
} from 'utils/checkInterface';
import { scrollToElement } from 'utils/scrollTo';

import Card from 'components/Card/Card';
import CardFundContent from 'components/CardFundContent/CardFundContent';
import SelectItemInput from 'components/SelectItemInput/SelectItemInput';
import SLSFSwitch from 'components/SLSFSwitch/SLSFSwitch';
import { IDonationFund } from 'models/donationFund';
import { IDonationOption } from 'models/donationOption';
import { Frequency } from 'models/frequency';

import DonateNowSummary from '../DonateNowForm/DonateNowSteps/DonateNowSummary';

import s from './InnovationFundDonationOptionsForm.module.scss';

type InnovationFundDonationOptionsFormProps = {
  onPurchase(
    value: IDonateNowForm,
    asAGuest: boolean,
    createNewAccountTabActive: boolean,
  ): void;
  className?: string;
  value: IDonateNowForm;
  onChange(wholeValue: any, key?: string, value?: any): void;
  onStepChange(step: DonateNowFormStep): void;
  donationFunds: IDonationFund[];
  donationOptions: IDonationOption[];
  loading?: boolean;
  cardProps?: any;
  step: DonateNowFormStep;
};

const InnovationFundDonationOptionsForm: React.FC<
  InnovationFundDonationOptionsFormProps
> = ({
  value: { frequency, customAmount, donationDay },
  loading,
  onChange,
  onPurchase,
  className,
  donationOptions,
  donationFunds,
  cardProps,
  step,
  onStepChange,
}) => {
  const containerRef = useRef<any>(null);
  useEffect(() => {
    if (containerRef?.current && step !== DonateNowFormSteps.selectDonation) {
      scrollToElement(containerRef?.current, 'top');
    }
  }, [step]);
  const stringToInt = (value: string) => parseInt(value, 10);

  const [donationOption, setDonationOption] = useState<IDonationOption>(
    donationOptions.find(el => {
      return el.default_ind === true;
    }) ?? ({} as IDonationOption),
  );

  const [fundOption, setFundOption] = useState<IDonationFund>(
    donationFunds.find(el => {
      return el.is_default === true;
    }) ?? ({} as IDonationFund),
  );

  const handleChange = (key: string, value: any) => {
    const wholeValue: IDonateNowForm = {
      donationOption: key === 'customAmount' ? undefined : donationOption,
      frequency,
      donationDay,
      customAmount: key === 'donationOption' ? undefined : customAmount,
      [key]: value,
    };
    switch (key) {
      case 'donationOption':
        setDonationOption(value);
        scrollToElement(containerRef?.current, 'bottom');
        wholeValue.customAmount = '';
        break;
      case 'customAmount':
        wholeValue.donationOption = '';
        setDonationOption(wholeValue.donationOption);
        break;
      case 'frequency':
        wholeValue.donationDay = Frequency.OnceOff === value ? undefined : 28;
        break;
      default:
    }
    onChange(wholeValue, key, value);
  };

  const renderCurrentStep = () => {
    switch (step) {
      case DonateNowFormSteps.selectDonation: {
        return (
          <div>
            <h2
              className={cx(
                'text-center font-weight-bold font-smaller',
                s['smaller-heading'],
              )}
            >
              Choose an innovation <br />
              project to support
            </h2>
            <Form.Group className="mb-0">
              <SelectItemInput
                noWrap
                value={fundOption}
                options={donationFunds}
                wrapperProps={{
                  className: 'mb-2',
                }}
              >
                {(props: IDonationFund, selected) => {
                  return (
                    <div
                      key={props?.id}
                      className="d-flex align-items-center font-weight-normal text-inherit"
                    >
                      <CardFundContent
                        key={props?.id}
                        className={cx(
                          { 'bg-primary': selected },
                          'border border-primary card-rounded cursor-pointer w-100',
                        )}
                      >
                        <Form.Check
                          type="radio"
                          value={props?.id}
                          className={cx('mr-3 position-absolute')}
                          checked={selected}
                          onChange={() => {
                            setFundOption(props);
                          }}
                        />
                        <h4
                          className={cx(
                            { 'text-white': selected },
                            'text-bold primary-dark-color ml-4',
                          )}
                        >
                          {props?.heading}
                        </h4>
                        <p
                          data-content="donation_campaign_option_heading"
                          className={cx(
                            { 'text-white': selected },
                            'pl-4 align-items-center',
                          )}
                        >
                          {props.description}
                        </p>
                      </CardFundContent>
                    </div>
                  );
                }}
              </SelectItemInput>
              <Row className="mt-4 px-0">
                <Col>
                  <h2
                    className={cx(
                      'text-center font-weight-bold font-smaller',
                      s['smaller-heading'],
                    )}
                  >
                    How much would you <br />
                    like to donate?
                  </h2>
                </Col>
              </Row>
              <Row className="justify-content-center mt-2 px-0">
                <SelectItemInput
                  noWrap
                  value={donationOption}
                  onChange={value => handleChange('donationOption', value)}
                  options={donationOptions}
                  wrapperProps={{
                    className: 'p-2 mr-2',
                  }}
                >
                  {(props: IDonationOption, selected) => {
                    return (
                      <Card
                        className={cx(
                          { 'bg-primary': selected },
                          'border rounded-lg center border-primary',
                        )}
                      >
                        <Card.Body className="p-1">
                          <Row className="m-1">
                            <Col key={props.id}>
                              <h4
                                className={cx(
                                  { 'text-white': selected },
                                  'text-bold mb-0 primary-dark-color',
                                )}
                              >
                                ${stringToInt(props?.amount?.toString())}
                              </h4>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    );
                  }}
                </SelectItemInput>
              </Row>
            </Form.Group>
            <Form.Group>
              <Row className="align-items-center mt-4 px-0">
                <Col lg={6} className="pr-md-0">
                  <Form.Label
                    className={cx(
                      s['custom-donation-label'],
                      'font-weight-bold text-center text-md-left d-block text-bold m-md-0 mb-3',
                    )}
                  >
                    Or choose a custom donation
                  </Form.Label>
                </Col>
                <Col lg={6}>
                  <div className="d-flex align-items-center">
                    <HiOutlineCurrencyDollar
                      size={32}
                      className="text-primary mr-1"
                    />
                    <Form.Control
                      required
                      type="number"
                      placeholder="enter amount"
                      value={customAmount}
                      min={1}
                      data-cy="input-donate-now"
                      className="square-amount-input px-1"
                      onChange={({ target: { value } }) => {
                        handleChange(
                          'customAmount',
                          Math.abs(stringToInt(value)),
                        );
                      }}
                    />
                  </div>
                </Col>
              </Row>
            </Form.Group>
            <Row>
              <Col className="text-center py-3">
                <Button
                  data-cy="button-next"
                  disabled={!(fundOption && (donationOption || customAmount))}
                  type="button"
                  onClick={() =>
                    onStepChange(DonateNowFormSteps.selectFrequency)
                  }
                >
                  Next
                </Button>
              </Col>
            </Row>
          </div>
        );
      }
      case DonateNowFormSteps.selectFrequency: {
        return (
          <div>
            <Form.Group className="mb-0 mb-3">
              <Card className="bg-primary text-white">
                <Card.Body>
                  <div className="mb-2">
                    <div className="d-flex justify-content-between mb-1">
                      <h4 className={cx('text-bold', 'm-0')}>
                        ${donationOption?.amount || customAmount}
                      </h4>
                      <Button
                        variant="link"
                        className=" text-white text-left p-0 text-button-link"
                        onClick={() =>
                          onStepChange(DonateNowFormSteps.selectDonation)
                        }
                      >
                        Change donation
                      </Button>
                    </div>
                    {fundOption ? (
                      <p>{fundOption.heading}</p>
                    ) : (
                      CUSTOM_AMOUNT_MESSAGE
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Form.Group>
            <Card>
              <Card.Body>
                <Form.Group className="m-0 pb-1">
                  <h4 className="text-center mb-2">
                    Thank you for your support
                  </h4>
                  <p className="mb-4 text-grey text-center">
                    Could you donate monthly and become a Guardian of the Surf?
                  </p>
                  <div className="d-flex justify-content-center align-items-center mb-3">
                    <h5 className={cx('text-right pr-3', s['donate-label'])}>
                      Once-off Donation
                    </h5>
                    <SLSFSwitch
                      checked={frequency === Frequency.Monthly}
                      onChange={value =>
                        handleChange(
                          'frequency',
                          value ? Frequency.Monthly : Frequency.OnceOff,
                        )
                      }
                    />
                    <h5 className={cx('text-left pl-3', s['donate-label'])}>
                      Monthly Donation
                    </h5>
                  </div>
                  <div className="px-3">
                    <p className="mb-2 mt-md-0 text-center">
                      Sarah 32, gives $52 monthly
                    </p>
                    <p className="text-center font-italic">
                      "Becoming a Guardian of the Surf was a real no-brainer for
                      me. Its the least I can do to help keep surf lifesavers on
                      our beaches and costs me less than a cup of coffee each
                      day"
                    </p>
                  </div>
                </Form.Group>
              </Card.Body>
            </Card>
            <div className="text-center py-3">
              <Button
                data-cy="button-next-thank-you"
                type="button"
                variant="outline-primary"
                className="mt-3"
                onClick={() =>
                  onStepChange(
                    frequency === Frequency.OnceOff
                      ? DonateNowFormSteps.summary
                      : DonateNowFormSteps.selectDonationDate,
                  )
                }
              >
                Next
              </Button>
            </div>
          </div>
        );
      }
      case DonateNowFormSteps.selectDonationDate: {
        return (
          <div>
            <Form.Group className="mb-0 mb-3">
              <Card className="bg-primary text-white">
                <Card.Body>
                  <div className="mb-2">
                    <div className="d-flex justify-content-between mb-1">
                      <h4 className={cx('text-bold m-0')}>
                        $
                        {stringToInt(donationOption?.amount?.toString()) ||
                          stringToInt(customAmount?.toString() || '0')}
                      </h4>
                      <Button
                        variant="link"
                        className=" text-white text-left p-0 text-button-link"
                        onClick={() =>
                          onStepChange(DonateNowFormSteps.selectDonation)
                        }
                      >
                        Change donation
                      </Button>
                    </div>
                    {fundOption ? (
                      <p>{fundOption.heading}</p>
                    ) : (
                      CUSTOM_AMOUNT_MESSAGE
                    )}
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <div>
                      <h5>Frequency</h5>
                      <h4 className={cx('text-bold m-0')}>
                        {frequency === Frequency.OnceOff
                          ? 'Single'
                          : Frequency.Monthly}
                      </h4>
                    </div>
                    <Button
                      variant="link"
                      className=" text-white text-left p-0 text-button-link"
                      onClick={() =>
                        onStepChange(DonateNowFormSteps.selectFrequency)
                      }
                    >
                      Change frequency
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Form.Group>
            <Card>
              <Card.Body>
                <Form.Group className="m-0 pb-1">
                  <h4 className="text-center mb-2">Choose day of donation</h4>
                  <p className="mb-4 text-grey text-center">
                    You can choose to make your donation on either the 15th or
                    28th of the month.
                  </p>
                  <div className="d-flex justify-content-center align-items-center mb-3">
                    <h5 className={cx('text-right pr-3', s['donate-label'])}>
                      15th of Month
                    </h5>
                    <SLSFSwitch
                      checked={donationDay === 28}
                      onChange={value =>
                        handleChange('donationDay', value ? 28 : 15)
                      }
                    />
                    <h5 className={cx('text-left pl-3', s['donate-label'])}>
                      28th of Month
                    </h5>
                  </div>
                  <div className="px-3">
                    <p className="mb-2 mt-md-0 text-center">
                      Mark 45, gives $100 monthly
                    </p>
                    <p className="text-center font-italic">
                      "Volunteer surf lifesavers saved my little boy from a rip
                      so I donate monthly, on a day that works best for me, as I
                      cannot thank them enough for what they have done"
                    </p>
                  </div>
                </Form.Group>
              </Card.Body>
            </Card>
            <div className="text-center mt-3">
              <Button
                type="button"
                variant="outline-primary"
                className="my-3"
                onClick={() => onStepChange(DonateNowFormSteps.summary)}
              >
                Next
              </Button>
            </div>
          </div>
        );
      }
      case DonateNowFormSteps.summary: {
        return (
          <DonateNowSummary
            donationOption={donationOption}
            donationFund={fundOption}
            onStepChange={onStepChange}
            onPurchase={onPurchase}
            frequency={frequency}
            customAmount={customAmount}
            donationDay={donationDay}
            canChangeFrequency
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <Card
      className={cx(
        'donate-now-card-mobile',
        'position-relative',
        'overflow-hidden',
        className,
      )}
      loading={loading}
      {...cardProps}
    >
      <Card.Body ref={containerRef}>
        <Form onSubmit={ev => ev.preventDefault()}>{renderCurrentStep()}</Form>
      </Card.Body>
    </Card>
  );
};

export default InnovationFundDonationOptionsForm;
